
import React from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useLanguage } from '@/contexts/LanguageContext';
import CustomDot from './CustomDot';
import CustomTooltip from './CustomTooltip';
import { SCATTER_CHART, CHART_LINE_STYLES } from '@/lib/chart-constants';
import { DCTOS_GRADIENTS } from '@/lib/dctos-config';

interface ChartData {
  name: string;
  fullName: string;
  value: number;
  color: string;
}

interface DesktopBehavioralChartProps {
  data: ChartData[];
  height?: number;
  compact?: boolean;
}

const DesktopBehavioralChart: React.FC<DesktopBehavioralChartProps> = ({
  data,
  height = 180,
  compact = false
}) => {
  const { t } = useLanguage();

  // Separate main DCTO traits from Style (S)
  const mainData = data.filter(d => d.name !== 'S' && d.name !== 'E');
  const styleData = data.find(d => d.name === 'S' || d.name === 'E');

  // Reverse data for desktop chart to show D at top
  const chartData = [...mainData].reverse();
  
  // Calculate margins and font sizes based on height and compact mode
  const isCompact = height <= 140 || compact;
  const margins = isCompact ? SCATTER_CHART.margins.compact : SCATTER_CHART.margins.default;
  const tickFontSize = isCompact ? SCATTER_CHART.tickFontSize.compact : SCATTER_CHART.tickFontSize.default;
  const labelFontSize = isCompact ? SCATTER_CHART.labelFontSize.compact : SCATTER_CHART.labelFontSize.default;

  // Calculate main chart height (reduce if we have style data)
  const mainChartHeight = styleData ? height - 32 : height;

  // Get gradient for Style trait
  const styleGradient = DCTOS_GRADIENTS.style;
  
  return (
    <div 
      style={{ height: `${height}px` }} 
      className="w-full bg-card rounded-none relative border border-border/30 overflow-hidden"
    >
      {/* Main DCTO Chart */}
      <div style={{ height: `${mainChartHeight}px` }} className="w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart 
            data={chartData} 
            margin={margins}
          >
            <XAxis 
              type="number" 
              domain={[0, 100]} 
              tickFormatter={value => `${value}%`} 
              axisLine={false} 
              tickLine={false} 
              tick={{
                fontSize: tickFontSize,
                fill: 'hsl(var(--muted-foreground))'
              }}
              ticks={isCompact ? [0, 50, 100] : [0, 20, 40, 60, 80, 100]} 
            />
            <YAxis 
              type="category" 
              dataKey="name"
              axisLine={false} 
              tickLine={false} 
              interval={0}
              tick={{
                fontSize: labelFontSize,
                fill: 'hsl(var(--foreground))',
                fontWeight: 600
              }}
              width={margins.left} 
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Average line at 50th percentile - space gray */}
            <ReferenceLine 
              x={50} 
              stroke={CHART_LINE_STYLES.reference.stroke}
              strokeWidth={CHART_LINE_STYLES.reference.strokeWidth}
              strokeDasharray={CHART_LINE_STYLES.reference.strokeDasharray}
              label={{
                value: t('chart.average'),
                position: "insideTopLeft",
                offset: 5,
                fontSize: isCompact ? 7 : 9,
                fill: "hsl(var(--muted-foreground))"
              }}
            />
            
            <Scatter 
              dataKey="value" 
              fill="hsl(var(--foreground))" 
              line={{
                stroke: "hsl(var(--foreground))",
                strokeWidth: CHART_LINE_STYLES.connecting.strokeWidth
              }}
              shape={<CustomDot />} 
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Style (S) Indicator - Separate section below with gradient dot */}
      {styleData && (
        <div className="h-8 relative border-t border-border/20">
          {/* S Label - positioned to match YAxis labels */}
          <div 
            className="absolute font-semibold text-foreground flex items-center justify-end"
            style={{ 
              fontSize: labelFontSize,
              left: 0,
              width: `${margins.left}px`,
              top: 0,
              bottom: 0
            }}
          >
            S
          </div>
          
          {/* Background track - spans the plot area */}
          <div 
            className="absolute h-px bg-border/30"
            style={{ 
              left: `${margins.left}px`,
              right: `${margins.right}px`,
              top: '50%'
            }}
          />
          
          {/* 50% reference marker */}
          <div 
            className="absolute h-3 bg-muted-foreground/30"
            style={{ 
              left: `calc(${margins.left}px + (100% - ${margins.left + margins.right}px) * 0.5)`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: '1px'
            }}
          />
          
          {/* Style dot with gradient - using same coordinate calculation as main chart */}
          <svg 
            className="absolute"
            style={{
              left: `calc(${margins.left}px + (100% - ${margins.left + margins.right}px) * ${styleData.value / 100})`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: '16px',
              height: '16px',
              overflow: 'visible'
            }}
          >
            <defs>
              <linearGradient id="styleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={styleGradient[0]} />
                <stop offset="100%" stopColor={styleGradient[1]} />
              </linearGradient>
            </defs>
            {/* White ring - outer circle */}
            <circle cx={8} cy={8} r={6} fill="white" stroke="url(#styleGradient)" strokeWidth={2} />
            {/* Inner gradient circle */}
            <circle cx={8} cy={8} r={4} fill="url(#styleGradient)" />
          </svg>
          
          {/* Percentage label - right aligned */}
          <div 
            className="absolute text-muted-foreground flex items-center justify-center"
            style={{ 
              fontSize: tickFontSize,
              right: 0,
              width: `${margins.right}px`,
              top: 0,
              bottom: 0
            }}
          >
            {Math.round(styleData.value)}%
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopBehavioralChart;
